import type { PublishedCard } from "@/cards/common/types";
import {
  getMainCardContent,
  normalizeMainCardTypeKey,
} from "@/cards/common/utils/app-deck/mainCardShellReaders";

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isSyntheticSeasonPageType = (type?: string) => {
  const key = normalizeMainCardTypeKey(type);
  return (
    key === "season_card_exps" ||
    key === "season_card_exp_list" ||
    key === "season_card_exp_creation" ||
    key === "season_card_normal_exps"
  );
};

const normalizeStatus = (raw?: unknown): string =>
  String(raw ?? "")
    .trim()
    .toLowerCase();

/** Draft / Scheduled / Release (Active and legacy Released count as Release). */
export const getSeasonSeriesStatus = (
  exp?: Record<string, unknown> | null
): "Draft" | "Scheduled" | "Release" => {
  if (!exp) return "Draft";
  const status = normalizeStatus(exp.status);

  if (status === "draft") return "Draft";
  if (status === "scheduled") {
    const raw = exp.release_date;
    if (raw == null || raw === "") return "Scheduled";
    const d = new Date(typeof raw === "number" ? raw : String(raw));
    if (!Number.isNaN(d.getTime()) && d.getTime() <= Date.now()) return "Release";
    return "Scheduled";
  }
  if (status === "release" || status === "released" || status === "active") return "Release";

  // Fallback: date-based like demo when status is missing
  const raw = exp.release_date;
  if (raw == null || raw === "") return "Release";
  const d = new Date(typeof raw === "number" ? raw : String(raw));
  if (Number.isNaN(d.getTime())) return "Release";
  return d.getTime() <= Date.now() ? "Release" : "Scheduled";
};

/** True when series content should appear as a book page. */
export const isSeasonSeriesReleased = (exp?: Record<string, unknown> | null): boolean =>
  getSeasonSeriesStatus(exp) === "Release";

/** Whether fans may see this row in the series list (hide Draft). */
export const isSeasonSeriesVisibleToFans = (exp?: Record<string, unknown> | null): boolean => {
  const status = getSeasonSeriesStatus(exp);
  return status === "Release" || status === "Scheduled";
};

/** Series rows live on top-level `content.exps`. */
export const findSeasonCardEmbeddedExps = (
  ...sources: Array<Record<string, unknown> | PublishedCard | null | undefined>
): Record<string, unknown>[] => {
  const byId = new Map<string, Record<string, unknown>>();

  const ingest = (rows: unknown[]) => {
    for (const row of rows) {
      if (!isObject(row)) continue;
      const id = row.id != null ? String(row.id) : JSON.stringify(row);
      byId.set(id, row);
    }
  };

  for (const source of sources) {
    if (!source) continue;

    if (Array.isArray((source as { exps?: unknown }).exps)) {
      ingest((source as { exps: unknown[] }).exps);
    }

    const topContent = getMainCardContent(source as Record<string, unknown>);
    if (Array.isArray(topContent.exps)) {
      ingest(topContent.exps as unknown[]);
    }

    if (isObject((source as { content?: unknown }).content)) {
      const content = (source as { content: Record<string, unknown> }).content;
      if (Array.isArray(content.exps)) ingest(content.exps as unknown[]);
    }

    const pages = Array.isArray((source as { pages?: unknown }).pages)
      ? ((source as { pages: Array<{ type?: string; content?: unknown }> }).pages)
      : [];
    for (const page of pages) {
      if (!isObject(page?.content)) continue;
      if (Array.isArray(page.content.exps)) {
        ingest(page.content.exps as unknown[]);
      }
      const pageType = normalizeMainCardTypeKey(String(page.type ?? ""));
      if (pageType === "season_card_exps") {
        ingest([page.content]);
      }
    }
  }

  return Array.from(byId.values());
};

/**
 * Builds book pages for season series from `content.exps`:
 * - `season_card_exp_list` — lineup (talent: all; fans: hide Draft)
 * - `season_card_exps` — talent: all statuses (incl. Draft); fans: Release only
 * - Fans without purchase get neither
 * - Talent can force include via `includeEpisodePages`
 */
export const enhanceSeasonCardForPreview = (
  card: PublishedCard | null,
  options?: {
    exps?: Record<string, unknown>[];
    includeEpisodePages?: boolean;
    isTalentView?: boolean;
  }
): PublishedCard | null => {
  if (!card) return null;

  const pages = Array.isArray(card.pages) ? card.pages : [];
  const basePages = pages.filter((page) => !isSyntheticSeasonPageType(page?.type));

  const isPurchased = (card as { purchase?: unknown }).purchase != null;
  const isTalent = Boolean(options?.includeEpisodePages || options?.isTalentView);

  if (!isPurchased && !isTalent) {
    return {
      ...card,
      pages: basePages as PublishedCard["pages"],
    };
  }

  const allExps =
    options?.exps && options.exps.length > 0
      ? options.exps
      : findSeasonCardEmbeddedExps(card as unknown as Record<string, unknown>);

  if (allExps.length === 0) {
    return {
      ...card,
      pages: basePages as PublishedCard["pages"],
    };
  }

  const listExps = isTalent ? allExps : allExps.filter((exp) => isSeasonSeriesVisibleToFans(exp));
  // Talent sees Draft / Scheduled / Release as book pages; fans only Release.
  const pageExps = isTalent ? allExps : allExps.filter((exp) => isSeasonSeriesReleased(exp));

  const maxOrder = Math.max(
    0,
    ...basePages.map((page) => Number((page as { order?: number })?.order ?? 0))
  );

  const listPage = {
    order: maxOrder + 0.05,
    type: "season_card_exp_list",
    content: { allEpisodes: listExps },
    assets: [] as number[],
  };

  const expPages = pageExps.map((exp, index) => ({
    order: maxOrder + (index + 1) * 0.1,
    type: "season_card_exps",
    content: exp,
    assets: [] as number[],
  }));

  return {
    ...card,
    pages: [...basePages, listPage, ...expPages] as PublishedCard["pages"],
  };
};
